import { CrashEvent, CrashEventPaginationDetails } from '@trace/types';
import { useContext, useEffect, useState } from 'react';
import { useQuery, UseQueryResult } from 'react-query';

import { ApiContext } from '../Api/Api';
import { URLStateContext } from '../URLState/URLState';
import appendURLSearchParams from '../../utils/appendURLSearchParams';
import getDateRange from '../../utils/time/getTimeRange';

type HookPayload = {
  crashEvent?: CrashEvent;
  pagination: CrashEventPaginationDetails;
};

export type HookResult = Pick<UseQueryResult, 'refetch' | 'isError' | 'isFetching'> & HookPayload;

/**
 * A hook that retrieves an individual CrashEvent for a given Crash, along with the IDs of previous/next CrashEvents
 * within the provided time range.
 *
 * @param  {string} crashId
 * @returns HookResult
 */
const useCrashEvent = (crashId: string, eventId?: string): HookResult => {
  const { get } = useContext(ApiContext);
  const { dateFrom, dateTo, duration } = useContext(URLStateContext);
  const { dateFrom: start, dateTo: end } = getDateRange(duration, dateFrom, dateTo);
  const [payload, setPayload] = useState<HookPayload>({
    pagination: {
      next: null,
      previous: null,
      offset: 0,
      total: 0,
    },
  });

  const { data, isError, isFetching, refetch } = useQuery<
    CrashEvent & {
      pagination: CrashEventPaginationDetails;
    }
  >(['crashEvent', { crashId, eventId, start, end }], async () =>
    !eventId
      ? undefined
      : get(
          `/issues/${crashId}/events/${eventId}?${appendURLSearchParams(
            '',
            ['start', start.toString()],
            ['end', end.toString()],
          )}`,
        ),
  );

  /**
   * Use a side-effect to:
   *  - Update the `pagination`, `crashEvent` state whenever the crashEvent query resolves
   */
  useEffect(() => {
    if (data) {
      const { pagination: dataPagination, ...dataCrashEvent } = data;

      setPayload({
        pagination: dataPagination,
        crashEvent: dataCrashEvent,
      });
    }
  }, [data]);

  return {
    isError,
    isFetching,
    refetch,
    ...payload,
  };
};

export default useCrashEvent;
